import { toast } from "sonner";
import { apiRefresh } from "@/lib/auth-api";
import { applyAuthSuccessBody, getAccessToken, getRefreshToken, logoutLocalAccount } from "@/lib/local-auth";

/** Harus sama dengan pesan di `backend/src/middleware/auth.ts`. */
export const INVALID_ACCESS_TOKEN_MESSAGE = "Invalid or expired access token";

function mergeAuthHeader(init: RequestInit | undefined, accessToken: string): RequestInit {
  const headers = new Headers(init?.headers ?? undefined);
  headers.set("Authorization", `Bearer ${accessToken}`);
  return { ...init, headers };
}

function isInvalidOrExpiredAccessTokenPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  return (payload as { error?: unknown }).error === INVALID_ACCESS_TOKEN_MESSAGE;
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSessionOnce(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;
  try {
    const body = await apiRefresh(rt);
    if (!body.user || !body.session) return false;
    applyAuthSuccessBody(body);
    return true;
  } catch {
    return false;
  }
}

function enqueueRefresh(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = refreshSessionOnce().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function sessionExpiredToast() {
  toast.error("Sesi berakhir, silakan masuk kembali.");
}

/**
 * Untuk endpoint yang memakai `requireAuth` di backend: menambahkan Bearer,
 * dan pada 401 karena access token kedaluwarsa memanggil refresh sekali lalu mengulang request.
 */
export async function authFetch(input: string | URL, init?: RequestInit): Promise<Response> {
  const url = typeof input === "string" ? input : String(input);
  let token = getAccessToken();
  if (!token) {
    throw new Error("Belum login atau token tidak tersedia.");
  }

  const send = (accessToken: string) => fetch(url, mergeAuthHeader(init, accessToken));

  let res = await send(token);
  if (res.status !== 401) {
    return res;
  }

  const errPayload = await res.clone().json().catch(() => null);
  if (!isInvalidOrExpiredAccessTokenPayload(errPayload)) {
    return res;
  }

  const refreshed = await enqueueRefresh();
  if (!refreshed) {
    logoutLocalAccount();
    sessionExpiredToast();
    return res;
  }

  token = getAccessToken();
  if (!token) {
    logoutLocalAccount();
    sessionExpiredToast();
    return res;
  }

  res = await send(token);
  if (res.status === 401) {
    const again = await res.clone().json().catch(() => null);
    if (isInvalidOrExpiredAccessTokenPayload(again)) {
      logoutLocalAccount();
      sessionExpiredToast();
    }
  }
  return res;
}
